// src/ui/components/discTimer.ts — Visual Disc Timer for ADHD Learners (FR-ADHD-001)
// Replaces ticking clock numbers with a shrinking colored circle to mitigate time blindness and anxiety.
// Color transitions: Green (>40%) -> Amber (20-40%) -> Coral/Red (<20%).
// Includes tap-to-reveal numeric overlay ("mm:ss left" for 2 seconds) and Zen Mode.

import { tokens } from '../designTokens';

export interface DiscTimerCallbacks {
  onTick?: (remainingSeconds: number, formattedTime: string) => void;
  onExpire?: () => void;
}

export interface DiscTimerOptions {
  durationSeconds?: number;
  size?: number; // Minimum 48px for touch target compliance (NFR-TOUCH-001)
  isZenMode?: boolean;
  callbacks?: DiscTimerCallbacks;
}

/** Computes the arc color based on remaining fraction */
export function calculateDiscColor(fractionRemaining: number): string {
  if (fractionRemaining > 0.4) {
    return tokens.color.seaweedSage; // #3D5A45 (Calm green)
  }
  if (fractionRemaining > 0.2) {
    return tokens.color.gildedBrass; // #E5A93C (Amber attention)
  }
  return tokens.color.madderCoral; // #FF6B4A (Urgent coral)
}

/** Formats seconds into mm:ss string */
export function formatTimeSeconds(seconds: number): string {
  const safeSecs = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(safeSecs / 60);
  const rem = safeSecs % 60;
  return `${String(mins).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
}

export class DiscTimer {
  private container: HTMLElement;
  private totalDuration: number;
  private remainingSeconds: number;
  private timerId: number | null = null;
  private running: boolean = false;
  private zenMode: boolean = false;
  private size: number;
  private callbacks: DiscTimerCallbacks;
  private overlayTimeout: ReturnType<typeof setTimeout> | null = null;
  private isOverlayVisible: boolean = false;

  constructor(options: DiscTimerOptions = {}) {
    this.totalDuration = Math.max(1, options.durationSeconds ?? 600);
    this.remainingSeconds = this.totalDuration;
    this.size = Math.max(48, options.size ?? 52); // Ensure >=48px for touch target
    this.callbacks = options.callbacks ?? {};

    // Load persisted Zen Mode preference from localStorage if not explicitly set
    if (typeof options.isZenMode === 'boolean') {
      this.zenMode = options.isZenMode;
    } else {
      try {
        this.zenMode = localStorage.getItem('clawey_zen_mode') === 'true';
      } catch {
        this.zenMode = false;
      }
    }

    this.container = document.createElement('div');
    this.container.className = 'disc-timer-container touch-target';
    this.container.setAttribute('role', 'timer');
    this.container.setAttribute('aria-label', 'Visual countdown timer. Tap to see remaining time.');
    this.container.style.cssText = `
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: ${this.size}px;
      height: ${this.size}px;
      min-width: 48px;
      min-height: 48px;
      cursor: pointer;
      user-select: none;
      touch-action: manipulation;
    `;

    this.attachEvents();
    this.render();
  }

  private attachEvents(): void {
    this.container.addEventListener('click', (e) => {
      e.stopPropagation();
      this.revealNumericTime();
    });
  }

  /** Temporarily display numeric time overlay for 2 seconds (FR-ADHD-001) */
  revealNumericTime(): void {
    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
      this.overlayTimeout = null;
    }

    this.isOverlayVisible = true;
    this.render();

    this.overlayTimeout = setTimeout(() => {
      this.isOverlayVisible = false;
      this.render();
    }, 2000);
  }

  /** Render the SVG Disc and overlay */
  private render(): void {
    const radius = 20;
    const circumference = 2 * Math.PI * radius;
    const fraction = this.remainingSeconds / this.totalDuration;
    const offset = circumference * (1 - Math.max(0, Math.min(1, fraction)));
    const strokeColor = calculateDiscColor(fraction);
    const formatted = this.getFormattedTime();

    if (this.zenMode) {
      this.container.innerHTML = `
        <div style="
          width: ${this.size}px;
          height: ${this.size}px;
          border-radius: 50%;
          border: 2px solid ${tokens.color.gildedBrass};
          background: rgba(255, 246, 229, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
          box-shadow: 0 2px 8px rgba(229, 169, 60, 0.2);
        " title="Zen Focus Mode: Time is hidden to keep you calm">
          🧘
        </div>
        ${this.renderOverlay(formatted)}
      `;
      return;
    }

    this.container.innerHTML = `
      <svg
        class="disc-timer-svg"
        viewBox="0 0 48 48"
        width="${this.size}"
        height="${this.size}"
        style="overflow: visible;"
      >
        <!-- Background Track -->
        <circle
          cx="24"
          cy="24"
          r="${radius}"
          fill="rgba(255, 253, 249, 0.9)"
          stroke="rgba(26, 54, 93, 0.12)"
          stroke-width="4"
        />
        <!-- Progress Arc -->
        <circle
          class="disc-timer-arc"
          cx="24"
          cy="24"
          r="${radius}"
          fill="none"
          stroke="${strokeColor}"
          stroke-width="4.5"
          stroke-linecap="round"
          stroke-dasharray="${circumference}"
          stroke-dashoffset="${offset}"
          transform="rotate(-90 24 24)"
          style="transition: stroke-dashoffset 0.8s ease, stroke 0.5s ease;"
        />
        <!-- Center Calm Icon -->
        <text
          x="24"
          y="26"
          text-anchor="middle"
          dominant-baseline="central"
          font-size="12"
          fill="${tokens.color.morrisIndigo}"
          font-weight="bold"
        >⏱️</text>
      </svg>
      ${this.renderOverlay(formatted)}
    `;
  }

  private renderOverlay(formatted: string): string {
    if (!this.isOverlayVisible) return '';

    return `
      <div
        id="disc-timer-overlay"
        style="
          position: absolute;
          bottom: -36px;
          left: 50%;
          transform: translateX(-50%);
          background: ${tokens.color.morrisIndigo};
          color: #FFFDF9;
          font-family: ${tokens.font.mono};
          font-size: 0.85rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 8px;
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(10, 25, 47, 0.25);
          pointer-events: none;
          z-index: 50;
          animation: fadeIn 0.15s ease;
        "
      >
        ${formatted} left
      </div>
    `;
  }

  /** Start or resume the timer */
  start(): void {
    if (this.running) return;
    this.running = true;

    this.timerId = window.setInterval(() => {
      this.tick();
    }, 1000);

    this.callbacks.onTick?.(this.remainingSeconds, this.getFormattedTime());
    this.render();
  }

  /** Advance countdown by 1 second */
  private tick(): void {
    if (!this.running) return;

    this.remainingSeconds -= 1;

    if (this.remainingSeconds <= 0) {
      this.remainingSeconds = 0;
      this.stop();
      this.render();
      this.callbacks.onTick?.(0, '00:00');
      this.callbacks.onExpire?.();
    } else {
      this.render();
      this.callbacks.onTick?.(this.remainingSeconds, this.getFormattedTime());
    }
  }

  /** Pause the countdown */
  pause(): void {
    this.stop();
  }

  /** Resume countdown */
  resume(): void {
    this.start();
  }

  /** Stop the interval */
  stop(): void {
    this.running = false;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  /** Reset timer */
  reset(newDurationSeconds?: number): void {
    this.stop();
    if (typeof newDurationSeconds === 'number' && newDurationSeconds > 0) {
      this.totalDuration = newDurationSeconds;
    }
    this.remainingSeconds = this.totalDuration;
    this.render();
    this.callbacks.onTick?.(this.remainingSeconds, this.getFormattedTime());
  }

  /** Toggle Zen Mode */
  toggleZenMode(): boolean {
    this.zenMode = !this.zenMode;
    try {
      localStorage.setItem('clawey_zen_mode', String(this.zenMode));
    } catch {
      // Ignored
    }
    this.render();
    return this.zenMode;
  }

  isZenMode(): boolean {
    return this.zenMode;
  }

  setZenMode(enabled: boolean): void {
    this.zenMode = enabled;
    this.render();
  }

  getRemainingSeconds(): number {
    return this.remainingSeconds;
  }

  getTotalDuration(): number {
    return this.totalDuration;
  }

  getFormattedTime(): string {
    return formatTimeSeconds(this.remainingSeconds);
  }

  getElement(): HTMLElement {
    return this.container;
  }

  destroy(): void {
    this.stop();
    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
      this.overlayTimeout = null;
    }
  }
}
